// Player controller — Quake/Source-style input → velocity
// Engine feature: reads player input, applies acceleration + friction + jump.
// Does NOT apply gravity, position updates, or collision — those are in physics.ts.

import { FP, type Fixed } from "./fixed.js";
import { type Vec3Store, type IntStore, type System, BitSet, query } from "./ecs.js";
import { simContext } from "./tick.js";
import { emptyInput } from "./input.js";
import { audioEvents, AudioEventType } from "./events.js";
import { groundMaterial } from "./physics.js";

export type PlayerControllerConfig = {
  maxSpeed: Fixed;
  groundAccel: Fixed;
  airAccel: Fixed;
  airSpeedCap: Fixed;
  friction: Fixed;
  stopSpeed: Fixed;
  jumpVelocity: Fixed;
};

export const DEFAULT_CONTROLLER: PlayerControllerConfig = {
  maxSpeed: FP.from(8),
  groundAccel: FP.from(10),
  airAccel: FP.from(100),    // high air accel for surf (Source sv_airaccelerate 150 equivalent)
  airSpeedCap: FP.from(2),
  friction: FP.from(8),
  stopSpeed: FP.from(2),
  jumpVelocity: FP.from(7),
};

const TICK_DT = FP.div(FP.ONE, FP.from(60));
const DIAG = 707;

const FOOTSTEP_INTERVAL = 20; // ticks between footsteps (~330ms)
const FOOTSTEP_MIN_SPEED_SQ = 4_000; // FP speed² threshold (~2 u/s)
const LAND_COOLDOWN = 15; // minimum ticks between land sounds

// PM_Accelerate — Quake movement core
function accelerate(
  vx: Fixed, vz: Fixed,
  wishX: Fixed, wishZ: Fixed,
  wishSpeed: Fixed, accel: Fixed
): { vx: Fixed; vz: Fixed } {
  const currentSpeed = FP.add(FP.mul(vx, wishX), FP.mul(vz, wishZ));
  const addSpeed = FP.sub(wishSpeed, currentSpeed);
  if (addSpeed <= 0) return { vx, vz };
  let accelSpeed = FP.mul(FP.mul(accel, TICK_DT), wishSpeed);
  if (accelSpeed > addSpeed) accelSpeed = addSpeed;
  return {
    vx: FP.add(vx, FP.mul(accelSpeed, wishX)),
    vz: FP.add(vz, FP.mul(accelSpeed, wishZ)),
  };
}

function applyFriction(vx: Fixed, vz: Fixed, friction: Fixed, stopSpeed: Fixed): { vx: Fixed; vz: Fixed } {
  const speed = FP.sqrt(FP.add(FP.mul(vx, vx), FP.mul(vz, vz)));
  if (speed < FP.from(0.05)) return { vx: FP.ZERO, vz: FP.ZERO };
  const control = speed < stopSpeed ? stopSpeed : speed;
  const drop = FP.mul(FP.mul(control, friction), TICK_DT);
  let newSpeed = FP.sub(speed, drop);
  if (newSpeed < 0) newSpeed = FP.ZERO;
  const factor = FP.div(newSpeed, speed);
  return { vx: FP.mul(vx, factor), vz: FP.mul(vz, factor) };
}

/**
 * Creates a player controller: input → velocity.
 * Reads ground state from a BitSet (written by the physics system).
 * Fires audio events for footsteps and landing.
 */
export function createPlayerController(
  position: Vec3Store,
  velocity: Vec3Store,
  playerId: IntStore,
  config: PlayerControllerConfig = DEFAULT_CONTROLLER,
  groundedState?: BitSet,
): System {
  const cfg = config;
  const footstepTimer = new Int32Array(4096);
  const wasGrounded = new Uint8Array(4096);
  const landCooldown = new Uint8Array(4096);

  return () => {
    const { ids, count } = query(playerId.mask, velocity.mask, position.mask);

    for (let i = 0; i < count; i++) {
      const id = ids[i];
      const input = simContext.inputs.get(playerId.data[id]) ?? emptyInput();

      let vx = velocity.x[id];
      let vy = velocity.y[id];
      let vz = velocity.z[id];

      const grounded = groundedState ? groundedState.has(id) : position.y[id] <= 0;
      const mat = groundMaterial[id];

      // Land detection: was airborne, now grounded
      if (landCooldown[id] > 0) landCooldown[id]--;
      if (grounded && !wasGrounded[id] && landCooldown[id] === 0) {
        audioEvents.push(AudioEventType.LAND, playerId.data[id],
          position.x[id], position.y[id], position.z[id], mat);
        landCooldown[id] = LAND_COOLDOWN;
      }
      wasGrounded[id] = grounded ? 1 : 0;

      // Build wish direction
      let forward = 0, strafe = 0;
      if (input.up) forward += FP.ONE;
      if (input.down) forward -= FP.ONE;
      if (input.left) strafe += FP.ONE;
      if (input.right) strafe -= FP.ONE;

      if (forward !== 0 && strafe !== 0) {
        forward = FP.mul(forward, DIAG);
        strafe = FP.mul(strafe, DIAG);
      }

      const sinY = FP.sin(input.yaw);
      const cosY = FP.cos(input.yaw);
      const negSinY = ((-sinY) | 0) as Fixed;

      const wishX = FP.add(FP.mul(forward, negSinY), FP.mul(strafe, cosY));
      const wishZ = FP.add(FP.mul(forward, cosY), FP.mul(strafe, sinY));

      const wishLen = FP.sqrt(FP.add(FP.mul(wishX, wishX), FP.mul(wishZ, wishZ)));
      let wishDirX = FP.ZERO, wishDirZ = FP.ZERO;
      if (wishLen > 0) {
        wishDirX = FP.div(wishX, wishLen);
        wishDirZ = FP.div(wishZ, wishLen);
      }

      if (grounded) {
        if (input.jump) vy = cfg.jumpVelocity;
        const fric = applyFriction(vx, vz, cfg.friction, cfg.stopSpeed);
        vx = fric.vx; vz = fric.vz;
        if (wishLen > 0) {
          const acc = accelerate(vx, vz, wishDirX, wishDirZ, cfg.maxSpeed, cfg.groundAccel);
          vx = acc.vx; vz = acc.vz;
        }
      } else {
        if (wishLen > 0) {
          const airWishSpeed = FP.min(cfg.maxSpeed, cfg.airSpeedCap);
          const acc = accelerate(vx, vz, wishDirX, wishDirZ, airWishSpeed, cfg.airAccel);
          vx = acc.vx; vz = acc.vz;
        }
      }

      // Footstep events — grounded + moving
      if (grounded) {
        const speedSq = FP.add(FP.mul(vx, vx), FP.mul(vz, vz));
        if (speedSq > FOOTSTEP_MIN_SPEED_SQ) {
          footstepTimer[id]++;
          if (footstepTimer[id] >= FOOTSTEP_INTERVAL) {
            footstepTimer[id] = 0;
            audioEvents.push(AudioEventType.FOOTSTEP, playerId.data[id],
              position.x[id], position.y[id], position.z[id], mat);
          }
        } else {
          footstepTimer[id] = 0;
        }
      } else {
        footstepTimer[id] = 0;
      }

      // E/Q noclip (debug)
      if (input.ascend) vy = FP.add(vy, FP.mul(cfg.maxSpeed, TICK_DT));
      if (input.descend) vy = FP.sub(vy, FP.mul(cfg.maxSpeed, TICK_DT));

      velocity.x[id] = vx;
      velocity.y[id] = vy;
      velocity.z[id] = vz;
    }
  };
}
